import { instance } from "api/apiBase";
import { Partner } from "types/landlord/partner";
import { PropertyManagementSystem } from "shared/types/partner";
import { objectToSnakeCase } from "utils/snakeCase";
import { SyncOption } from "views/settings-integration/integrations/pms/constants";

/**
 * Need to define properties for renter and tenancy summary
 */
export type DashboardPayload = {
  billing: {
    ach_link: string;
    bank_account_required: boolean;
    plaid_link: string;
    opaque_id: string;
  };
  algolia: {
    client_search_key?: string | null;
    index_name?: string | null;
  };
  property_management_system: {
    name: PropertyManagementSystem | "";
    last_synced_at: string | null;
  };
  status: "started" | "approved" | "rejected";
  property_summary: {
    occupied: number;
    total: number;
  };
  notifications: {
    payment_verifications?: number;
    identity_verifications?: number;
  };
};

export const getDashboard = async (): Promise<DashboardPayload> => {
  return (await instance.get(`/portal/v1/dashboard`)).data;
};

export const addTeamMember = async ({
  name,
  email,
  role_id
}: {
  name: string;
  email: string;
  role_id: number;
}) => {
  return (await instance.post(`/portal/v1/users`, { name, email, role_id }))
    .data;
};

export const resendInviteForTeamMember = async ({
  userId
}: {
  userId: string;
}) => {
  return (await instance.post(`portal/v1/users/${userId}/reset_password`)).data;
};

export const resetPermissionInviteForTeamMember = async ({
  userId
}: {
  userId: string;
}) => {
  return (await instance.post(`/portal/v1/users/${userId}/reset_permissions`))
    .data;
};

type SettingsPayload = {
  settings?: {
    name: string;
    readonly: boolean;
    value: string | null;
    title: string;
  }[];
};
export const QUERY_SETTINGS = "settings";
export const getSettings = async (): Promise<SettingsPayload> =>
  (await instance.get(`/portal/v1/settings`)).data;

export type SaveSettingsPayload = {
  resmanApiKey?: string;
  resmanPartnerId?: string;
  resmanAccountId?: string;
  buildiumClientId?: string;
  buildiumClientSecret?: string;
  buildiumEmail?: string;
  buildiumPassword?: string;
  buildiumSubdomain?: string;
  buildiumSync?: SyncOption;
  rentManagerCompanyCode?: string;
  rentManagerSync?: SyncOption;
  rentManagerEnableApplicationCharge?: boolean;
  rentManagerApplicationChargeCode?: string;
  rentManagerEnableTemplateFeeCharges?: boolean;
  rentManagerAdministrationFeeChargeCode?: string;
  rentManagerHoldingFeeChargeCode?: string;
  rentersWarehouseApiKey?: string;
  rentersWarehouseUrl?: string;
  rentersWarehouseSalesforceDomain?: string;
  rentersWarehouseSalesforceClientId?: string;
  rentersWarehouseSalesforceClientSecret?: string;
  rentersWarehouseSalesforceUsername?: string;
  rentersWarehouseSalesforcePassword?: string;
  manageAmericaCompanyId?: string;
  yardiWebServiceUrl?: string;
  yardiUsername?: string;
  yardiPassword?: string;
  yardiServerName?: string;
  yardiDatabase?: string;
  yardiEnableApplicationCharge?: boolean;
  yardiApplicationChargeCode?: string;
  yardiEnableScreeningServicesCharge?: boolean;
  yardiScreeningServicesChargeCode?: string;
  yardiGeneralLedgerAccount?: string;
  yardiEnableTemplateFeeCharges?: boolean;
  yardiAdministrationFeeChargeCode?: string;
  yardiHoldingFeeChargeCode?: string;
  yardiUseLeaseTerm?: boolean;
  rentvineAccount?: string;
  rentvineApiKey?: string;
  rentvineApiSecret?: string;
  rentvineSync?: SyncOption;
  quextCustomerId?: string;
  appfolioUsername?: string;
  appfolioPassword?: string;
  appfolioUrl?: string;
  appfolioSync?: string;
  appfolioUseReports?: boolean;
  appfolioUsePropertyGroups?: boolean;
  appfolioSelectedPropertyGroups?: string[];
  appfolioEnableRently?: boolean;
  appfolioUseSupergoodApi?: boolean;
  appfolioSupergoodUsername?: string;
  appfolioSupergoodPassword?: string;
  appfolioSupergoodMfaPhone?: string;
  appfolioEnableResidentTagging?: boolean;
  entrataDomain?: string;
  entrataUsername?: string;
  entrataPassword?: string;
  entrataMfaEmail?: string;
  accessKey?: string;
  appfolio_create_guest_cards?: boolean | string;
  pms_prospect_creation_trigger?: "decision" | "submission" | "start" | "lead";
  pms_prospect_creation_on_approve?: boolean;
  pms_prospect_creation_on_conditional_approve?: boolean;
  pms_prospect_creation_on_decline?: boolean;
  pms_import_inventory?: boolean;
  rent_manager_create_twa?: boolean;
  rent_manager_push_inbox_communications?: boolean;
  rent_manager_push_lead_stage_changes?: boolean;
  googleAnalyticsId?: string | string[] | null;
  propertywareClientId?: string;
  propertywareClientSecret?: string;
  propertywareSystemId?: string;
  propertywareSync?: SyncOption;
  propertywareEmail?: string;
  propertywarePassword?: string;
  propertywareMfaPhone?: string;
  revelaSubdomain?: string;
  propertywareUsePropertyGroups?: boolean;
  submit_boom_finance_apps_trigger?: string | null;
};

export const saveSettings = async (
  saveSettingsPayload: SaveSettingsPayload,
  options?: { cleanupAssetUnits?: boolean }
) => {
  return (
    await instance.post(`/portal/v1/settings`, {
      settings: objectToSnakeCase(saveSettingsPayload),
      ...(options?.cleanupAssetUnits ? { cleanup_asset_units: true } : {})
    })
  ).data;
};

type CurrentPartnerPayload = {
  partner: Partner;
};
export const QUERY_CURRENT_PARTNER = "current_partner";
export const getCurrentPartner = async (): Promise<CurrentPartnerPayload> =>
  (await instance.get(`portal/v1/partners/current`)).data;

type PartnersCredentialsPayload = {
  partner: {
    jwt_overriden_key: string | null;
  };
};

export const getPartnersCredentials =
  async (): Promise<PartnersCredentialsPayload> => {
    return (await instance.get(`/portal/v1/partners/credentials`)).data;
  };

export type StateProvinceDefaultPayload = {
  id: number;
  state_province: string;
  default_enrollment_type: string;
};

export type PropertyDefaultEnrollmentsPayload = {
  id: number;
  property_id: string;
  default_enrollment_type: string;
  property_name: string | null;
  address1?: string | null;
};

export type PartnerDefaultEnrollmentsPayload = {
  default_enrollment_type: string;
  state_province_defaults: StateProvinceDefaultPayload[];
  property_defaults: PropertyDefaultEnrollmentsPayload[];
};

export const getOperatingStatesAndProvinces = async (): Promise<{
  operating_states_and_provinces: string[];
}> => {
  return (
    await instance.get(`/portal/v1/partners/operating_states_and_provinces`)
  ).data;
};

export const getPartnerDefaultEnrollments =
  async (): Promise<PartnerDefaultEnrollmentsPayload> => {
    return (await instance.get(`/portal/v1/partners/default_enrollments`)).data;
  };

export type EnrollmentSettings = {
  default_enrollment_type: string;
  states_and_provinces: Array<{
    state_province: string;
    default_enrollment_type: string;
  }>;
  properties: Array<{
    property_id: string;
    default_enrollment_type: string;
  }>;
};

export const updatePartnerDefaultEnrollments = async (
  data: EnrollmentSettings
) => {
  return (await instance.patch(`/portal/v1/partners/default_enrollments`, data))
    .data;
};

export type UpdateAutoEnrollmentSettingsPayload = {
  enabled?: boolean;
  auto_enrollment_default?: "opt_in" | "opt_out" | "direct" | "default";
  enrollment_eligibility_date?: string | null;
};

export const updateAutoEnrollmentSettings = async (
  data: UpdateAutoEnrollmentSettingsPayload
) => {
  return (
    await instance.patch(`/portal/v1/partners/auto_enrollment_settings`, data)
  ).data;
};

export const getHubSpotAuthorizationUrl = async (): Promise<{
  authorization_url: string;
}> => {
  return (await instance.get(`/portal/v1/hubspot/authorization_url`)).data;
};

export const deleteHubSpotIntegration = async (): Promise<void> => {
  return instance.delete(`/portal/v1/hubspot`);
};
